import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import parse from "date-fns/parse";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { resources } from "@constants/hardcoded-strings";
import { SERVER_DATE_FORMAT } from "@constants/date-formats";
import { ButtonTypes } from "@enums/button-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { TimeRangeTypes } from "@enums/time-range-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { FraudReportSearchFiltersState } from "@idw-types/state/fraud-report-state";
import {
    exportFraudReportReduxRequest,
    getFraudReportReferenceDataReduxRequest,
    getFraudReportReduxRequest
} from "@redux-requests/fraud-report";
import { setFraudReportSearchFilters } from "@redux/actions/fraud-report-actions";
import { resetTableSettingsTableState } from "@redux/actions/table-settings-actions";
import { extractRelyingPartiesForPropertyName } from "@utils/permissions-util";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { initialFraudReportState } from "@redux/reducers/fraud-report-reducer";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { CardContainer } from "@containers/common/card-container/card-container";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { FraudReportSearchForm } from "./fraud-report-search-form/fraud-report-search-form";
import { FraudReportTableContainer } from "./fraud-report-table-container/fraud-report-table-container";
import { getFraudReportState, getSessionState, getTableSettingsState } from "@redux/selectors/root-selectors";

export const FraudReportContainer = () => {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();

    const { session } = useSelector(getSessionState);
    const { fraudReport: fraudReportTableState } = useSelector(getTableSettingsState);
    const {
        referenceData,
        searchFilters,
        searchResults,
        fileExport
    } = useSelector(getFraudReportState);
    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getFraudReportReferenceDataReduxRequest(dispatch)();
        }
    }, [referenceData.isLoading, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.companies &&
            referenceData.relyingParties &&
            referenceData.rpServices &&
            referenceData.assertions &&
            referenceData.riskLevels
        );
    }, [
        referenceData.companies,
        referenceData.relyingParties,
        referenceData.rpServices,
        referenceData.assertions,
        referenceData.riskLevels
    ]);

    const isLoading = useMemo(() => {
        return referenceData.isLoading;
    }, [referenceData.isLoading]);

    const isLoaded = useMemo(() => {
        return !isLoading && isReferenceDataPresent && !shouldLoadReferenceData;
    }, [isLoading, isReferenceDataPresent, shouldLoadReferenceData]);

    const allowedRelyingParties = useMemo(() => {
        if (referenceData.relyingParties) {
            return extractRelyingPartiesForPropertyName(
                UserPermissionTypes.VIEW_WORKFLOW_USAGE_DETAILS,
                session.user.axnAdminUserRPProperties,
                referenceData.relyingParties
            );
        }

        return [];
    }, [
        session.user.axnAdminUserRPProperties,
        referenceData.relyingParties
    ]);

    const allowedServices = useMemo(() => {
        if (referenceData.rpServices) {
            const rpIds = allowedRelyingParties.map(rp => rp.id);
            return referenceData.rpServices.filter(service => rpIds.includes(service.relyingPartyID));
        }

        return [];
    }, [
        allowedRelyingParties,
        referenceData.rpServices
    ]);

    useEffect(() => {
        if (Array.from(searchParams.entries()).length > 0 && isLoaded) {
            const shouldSearchOnLoad = isStringTrue(searchParams.get("shouldSearchOnLoad"));

            const newSearchFilters: FraudReportSearchFiltersState = {
                assertionIds: searchParams.get("assertionIds")?.split(",") || initialFraudReportState.searchFilters.assertionIds,
                companyId: searchParams.get("companyId") || initialFraudReportState.searchFilters.companyId,
                relyingPartyIds: searchParams.get("relyingPartyIds")?.split(",") || initialFraudReportState.searchFilters.relyingPartyIds,
                riskLevelIds: searchParams.get("riskLevelIds")?.split(",") || initialFraudReportState.searchFilters.riskLevelIds,
                rpServiceIds: searchParams.get("rpServiceIds")?.split(",") || initialFraudReportState.searchFilters.rpServiceIds,
                timeRangeType: (searchParams.get("timeRangeType") || initialFraudReportState.searchFilters.timeRangeType) as TimeRangeTypes,
                startDate: searchParams.has("startDate")
                    ? parse(searchParams.get("startDate"), SERVER_DATE_FORMAT, new Date())
                    : initialFraudReportState.searchFilters.startDate,
                endDate: searchParams.has("endDate")
                    ? parse(searchParams.get("endDate"), SERVER_DATE_FORMAT, new Date())
                    : initialFraudReportState.searchFilters.endDate
            };

            if (shouldSearchOnLoad) {
                handleSearch(newSearchFilters);
            } else {
                dispatch(setFraudReportSearchFilters(newSearchFilters));
            }
        }
    }, [searchParams, isLoaded]);

    const handleSearch = (newSearchFilters: FraudReportSearchFiltersState) => {
        const { assertionIds, companyId, relyingPartyIds, riskLevelIds, rpServiceIds, startDate, endDate } = newSearchFilters;
        
        dispatch(resetTableSettingsTableState(TableSettingsTableTypes.FRAUD_REPORT));
        dispatch(setFraudReportSearchFilters(newSearchFilters));

        getFraudReportReduxRequest(dispatch)(
            companyId,
            rpServiceIds,
            relyingPartyIds,
            assertionIds,
            riskLevelIds,
            startDate,
            endDate,
            referenceData.assertions,
            referenceData.riskLevels
        );
    };

    const onClickExport = async () => {
        exportFraudReportReduxRequest(dispatch)(
            searchFilters.companyId,
            searchFilters.relyingPartyIds,
            searchFilters.rpServiceIds,
            searchFilters.assertionIds,
            searchFilters.riskLevelIds,
            searchFilters.startDate,
            searchFilters.endDate,
            referenceData.assertions,
            referenceData.relyingParties,
            referenceData.riskLevels,
            referenceData.rpServices
        );
    };

    const headerButton = useMemo(() => {
        return (
            <ButtonWithSpinner
                id="fraud-report-export-button"
                label={resources.pages.fraudReport.headerButton}
                onClick={onClickExport}
                type={ButtonTypes.BUTTON}
                variant={"primary"}
                disabled={!isLoaded || searchResults.isLoading || fileExport.isLoading || !searchResults.assertionFraudResults?.length}
                hideSpinner={!fileExport.isLoading}
            />
        );
    }, [isLoaded, searchResults.isLoading, fileExport.isLoading, searchResults.assertionFraudResults]);

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.fraudReport.header} headerButton={headerButton} />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <FraudReportSearchForm
                        requestInProgress={searchResults.isLoading}
                        companies={referenceData.companies}
                        relyingParties={allowedRelyingParties}
                        rpServices={allowedServices}
                        assertions={referenceData.assertions}
                        riskLevels={referenceData.riskLevels}
                        searchFilters={searchFilters}
                        handleSearch={handleSearch}
                    />
                )}
                {isLoaded && !searchResults.isLoading && searchResults.assertionFraudResults && (
                    <FraudReportTableContainer
                        assertionFraudResults={searchResults.assertionFraudResults}
                        tableState={fraudReportTableState}
                    />
                )}
                {isLoaded && searchResults.isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};